import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";

import { RootState } from "../../../globals";
import HttpManager from "../../../services/HttpManager";
import { Bandeja, NumeroWa, usarRutaWappchat } from "../../../services/v2/prospects.service";
import SalesController, { AreaOpcion } from "./salesController";
import BandejaView from "./bandejaView";
import { TelefonoPanel } from "./telefono/telefonoUI";
import VentasBarra from "./ventasBarra";
import { push } from "./push";
import { useAltoVisible } from "./altoVisible";
import "./sales.css";
import "./crm.css";

/**
 * WappChat (antes "Mensajería") como sección propia del menú Ventas, ruta /wappchat (Esteban, 2026-10-03).
 * Arriba solo tres pestañas (Esteban, 2026-10-05): CRM, WappChat y WappTeam (el chat interno, que desde
 * 2026-10-09 es su propia sección /wappteam). El área (Mis clientes, Caja, Kits…) es un
 * selector dentro de la lista y solo aparece a quien tiene más de una. La persona abierta va en la URL (?id=N).
 * Quién la ve lo decide la sección 72; las llamadas siguen yendo a prospects.
 */
const DE_BANDEJA = ["caja", "kits", "academico", "rrhh"];

const WappchatController: React.FC = () => {
	const navigate = useNavigate();
	const http = HttpManager.getInstance();
	// La ficha de la persona va por /wappchat mientras esta pantalla está abierta (Caja, Kits y Académico no entran al CRM).
	useEffect(() => { usarRutaWappchat(true); return () => usarRutaWappchat(false); }, []);
	// Bandejas del usuario según sus roles: "Mis clientes" (Ventas/Admisiones) y las de área (Caja, Kits, Académico...).
	const [bandejas, setBandejas] = useState<Bandeja[] | null>(null);
	// Kits: también la lista de matriculados de su territorio (todas sus conversaciones, por cualquier número), como en el CRM.
	const [matriculados, setMatriculados] = useState<boolean>(false);
	// Filtro "Número" (solo Super Admin): todos los números encendidos; cada lista usa los de sus departamentos.
	const [numeros, setNumeros] = useState<NumeroWa[] | null>(null);
	const [params, setParams] = useSearchParams();
	// ?dept=caja (el chat de un alumno abierto desde Estudiantes/Horarios en otra pestaña, 2026-10-18): abre en esa bandeja
	// aunque la última vez se haya usado otra.
	const [modo, setModo] = useState<string>(() => { const d = params.get("dept"); if (d) return d; try { return localStorage.getItem("wchat.modo") || ""; } catch { return ""; } });
	// El chat interno ahora es WappTeam (/wappteam); los enlaces viejos con ?equipo=1 se mandan allá.
	useEffect(() => { if (params.get("equipo") === "1") navigate("/wappteam", { replace: true }); }, []);
	const inicial = Number(params.get("id")) || null;
	// La bandeja lleva su propia persona; aquí solo se refleja en la URL para la pestaña CRM y para recargar.
	const [sel, setSel] = useState<number | null>(inicial);
	const sessionCampus: any = useSelector((state: RootState) => state.rootReducer.sessionState.currentCampus);
	const legacyCampusKey: any = useSelector((state: RootState) => state.currentUserCampus.value);
	const legacyCampusRef: any = useSelector((state: RootState) => state.campusRef.value);
	const legacyCampus: any = legacyCampusKey && legacyCampusRef ? legacyCampusRef[legacyCampusKey] : null;
	const campusId: number | null = legacyCampus?.id ?? sessionCampus?.id ?? null;

	// Una conexión de avisos al instante mientras WappChat está abierto (sin consultar cada 15 s).
	useEffect(() => (campusId ? push.usar(campusId) : undefined), [campusId]);
	// WappTeam: cuántos mensajes sin leer (la pestaña de arriba). El mismo número que la barra de arriba (contadores.ts),
	// que también baja cuando se leen en una ventanita de abajo; antes solo se volvía a contar con un mensaje nuevo.
	const [abrirCon, setAbrirCon] = useState<number | null>(null);

	const cargarBandejas = useCallback(() => {
		if (!campusId) return;
		http.prospectsService.bandejas(campusId).then((r) => {
			setMatriculados(!!r.data.matriculados);
			setNumeros(r.data.numeros || null);
			// La primera vez con la lista de Matriculados, Kits arranca ahí aunque antes hubiera elegido su bandeja.
			try {
				if (r.data.matriculados && !localStorage.getItem("wchat.matriculados")) {
					localStorage.setItem("wchat.matriculados", "1");
					setModo("cartera"); localStorage.setItem("wchat.modo", "cartera");
				}
			} catch { /* sin almacenamiento */ }
			setBandejas(r.data.bandejas || []);
		}).catch(() => setBandejas([]));
	}, [campusId]);
	useEffect(() => { cargarBandejas(); }, [cargarBandejas]);
	const deVentas = (bandejas || []).some((b) => b.dept === "ventas" || b.dept === "admisiones");
	const cartera = deVentas || matriculados;
	// La pestaña CRM y el teléfono salen desde el primer momento (con el menú, igual que en WappTeam): antes esperaban a
	// las bandejas y al aparecer empujaban las pestañas (Esteban, 2026-10-12: "el salto extraño").
	const menuRef: any = useSelector((state: RootState) => (state as any).menuRef?.value);
	const veCrm = cartera || (bandejas === null && !!menuRef?.prospects);
	const deArea = (bandejas || []).filter((b) => DE_BANDEJA.includes(b.dept));
	// Lo elegido tiene que ser una de sus áreas; si no, la primera que tenga. Sin ninguna (Profesor, RRHH…): solo Equipo.
	const opciones = [...(cartera ? ["cartera"] : []), ...(deArea.length > 1 ? ["todas"] : []), ...deArea.map((b) => b.dept)];
	const modoActual = bandejas === null ? null : opciones.includes(modo) ? modo : opciones[0] || null;
	// Sin ninguna bandeja de clientes (Profesor, RRHH…) lo suyo es WappTeam.
	useEffect(() => { if (bandejas !== null && !opciones.length) navigate("/wappteam", { replace: true }); }, [bandejas]);
	const areas: AreaOpcion[] = opciones.map((o) => {
		const b = deArea.find((x) => x.dept === o);
		return {
			key: o, label: o === "cartera" ? (deVentas ? "Mis clientes" : "Clientes") : o === "todas" ? "Todas las áreas" : b?.nombre || o,
			n: o === "todas" ? deArea.reduce((t, x) => t + (x.sinContestar || 0), 0) : b?.sinContestar || 0,
		};
	});
	const elegir = (m: string) => { setModo(m); setSel(null); setAbrirCon(null); try { localStorage.setItem("wchat.modo", m); } catch { /* sin almacenamiento */ } };

	useEffect(() => {
		const actual = Number(params.get("id")) || null;
		if (actual !== sel) setParams(sel ? { id: String(sel) } : {}, { replace: true });
	}, [sel]);

	const raiz = useRef<HTMLDivElement>(null);
	useAltoVisible(raiz, campusId);
	// El auto-marcador abre a la persona aquí mismo, en su conversación (abrirPersona cambia en cada render: va por ref).
	const abrirRef = useRef<(id: number) => void>(() => undefined);

	const verCrm = (id: number | null) => navigate("/prospects" + (id ? "?id=" + id : ""));
	/** Abrir a una persona desde Equipo o desde una llamada entrante: en su conversación de clientes. */
	const abrirPersona = (id: number) => {
		const m = opciones.includes("cartera") ? "cartera" : opciones[0];
		if (!m) return;
		setModo(m); setAbrirCon(id); setSel(id);
	};

	abrirRef.current = abrirPersona;
	const abrirDesdeMarcador = useCallback((id: number) => abrirRef.current(id), []);
	return (
		<div className="crm" ref={raiz}>
			<VentasBarra activo="msj" campusId={campusId} veCrm={veCrm} veWappchat veWappteam
				onCrm={() => verCrm(sel)} onWappchat={() => undefined} onWappteam={() => navigate("/wappteam")} onAbrirMarcador={abrirDesdeMarcador} />
			<div className="crm-cuerpo">
				{/* Cambiar de plantel vuelve a empezar la bandeja (la persona puede no ser de ese plantel). */}
				{bandejas === null || !campusId ? <div className="ventas"><div className="ventas-vacio">Cargando…</div></div>
					: modoActual === "cartera" ? <SalesController key={(campusId || 0) + "-" + (abrirCon || 0)} inicial={abrirCon || inicial} onSel={setSel} onVerCrm={(id) => verCrm(id)}
						areas={areas} area={modoActual} onArea={elegir} numeros={numeros ? numeros.filter((n) => n.dept === "ventas" || n.dept === "admisiones") : null} />
					: modoActual ? <BandejaView key={(campusId || 0) + modoActual + "-" + (abrirCon || 0)} campusId={campusId} bandejas={deArea} dept={modoActual} inicial={abrirCon || inicial}
						onSel={setSel} onConteos={cargarBandejas} areas={areas} onArea={elegir}
						numeros={numeros ? numeros.filter((n) => (modoActual === "todas" ? DE_BANDEJA.includes(n.dept) : n.dept === modoActual)) : null} />
					: null}
			</div>
			{campusId && cartera ? <TelefonoPanel campusId={campusId} onAbrir={abrirPersona} /> : null}
		</div>
	);
};

export default WappchatController;
